import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { map, flatMap, catchError, retry } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { BookmarkModel } from './models/bookmark.model';
import { Observable, forkJoin, throwError, of } from 'rxjs';
import { StoryModel } from '../story/models/story.model';
import { BookmarkEntryModel } from './models/bookmark.entry.model';
import { BookmarkGroupModel } from './models/bookmark.group.model';
import { SerializeModuleService } from '../edit/serialize-module.service';
import { TrimConfiguration } from 'src/app/edit/models/trim-config.model';
import { ExploreService } from '../explore/explore.service';
import { ContentType } from '../story/models/content-type.enum';

import { endsWith, get } from 'lodash';
import { Store } from '@ngxs/store';
import { StoryState } from '../story/state/story.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import { StoryService } from '../story/story.service';
import { BookmarkFlagsEnum } from './models/bookmark.flags.enum';
import { BookmarkCategory } from './models/bookmark-category.enum';
import { BookmarkLimitModel } from './models/bookmark-limit.model';
import { BookmarkViewModeTag } from './models/bookmark-view-mode.enum';
import { normalizeBookmarkModel } from './lib/bookmark-utils';

const bookmarkServiceURL = '/ws/user/bookmark/v1';

const formHeaders = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class BookmarksService {
  constructor(
    private store: Store,
    private http: HttpClient,
    private serializeModuleService: SerializeModuleService,
    private exploreService: ExploreService,
    private storyService: StoryService,
  ) {}

  /**
   * Gets the bookmark data by the specified id
   * @param id The bookmark id
   */
  getBookmarkData(id: string): Observable<StoryModel> {
    return this.http.get(`/search/bookmarks/data?be=${id}`).pipe(
      retry(2), // Service randomly 502s
      map((result: any) => {
        result = result.bookmark;
        result.moduleId = result.customData
          ? result.customData.moduleId
          : result.modules.activeModules[0];

        return plainToClass(StoryModel, result as Object);
      }),
    );
  }

  /**
   * Gets the bookmark model data by specified id
   * @param id The bookmark id
   */
  getBookmarkModel(id: string): Observable<BookmarkModel> {
    return this.http.get(`/ws/search/bookmarks?eid=${id}`).pipe(
      retry(2), // Service randomly 502s
      map((result: any) => plainToClass(BookmarkModel, result.bookmark_info as object)),
      map((bookmark: BookmarkModel) => normalizeBookmarkModel(bookmark)),
    );
  }

  /** Gets all user generated bookmarks by specified category
   * @param category Category of bookmarks
   * @param query Optional search query
   */
  getUserBookmarks(category: BookmarkCategory, query: string = ''): Observable<BookmarkModel[]> {
    return this.http.get(`/ws/search/bookmarks/${category}?q=${query}`).pipe(
      retry(2), // Service randomly 502s
      map((results: object[]) => {
        return results
          .map((result) => plainToClass(BookmarkModel, result))
          .map((bookmark) => normalizeBookmarkModel(bookmark));
      }),
    );
  }

  /**
   * Gets a list of user mapped custom conditions.
   */
  getUserConditions(
    category: BookmarkCategory,
    query = '',
    id?: number,
  ): Observable<BookmarkModel[]> {
    let moduleAccessorLevel = '';
    switch (category) {
      case BookmarkCategory.Private:
        moduleAccessorLevel = 'user';
        break;
      case BookmarkCategory.Group:
        moduleAccessorLevel = 'group';
        break;
      default:
        break;
    }
    return this.http.get(`/ws/search/conditions/private?q=${query}`).pipe(
      // restrict by type + id
      map((results: Object[]) => {
        return results.filter((result: any) => {
          const matchedByType = result.module_accessor_level === moduleAccessorLevel;
          const matchedById = !id || result.module_accessor_id === id;
          return matchedByType && matchedById;
        });
      }),
      // to bookmarks
      map((results: Object[]) => plainToClass(BookmarkModel, results)),
      // normalize
      map((bookmarks: BookmarkModel[]) => {
        return bookmarks.map((bookmark) => normalizeBookmarkModel(bookmark));
      }),
    );
  }

  /**
   * Gets a list of private bookmarks only accessible to user
   * @param query Optional search query
   */
  getPrivateBookmarks(query: string = ''): Observable<BookmarkModel[]> {
    return this.getUserBookmarks(BookmarkCategory.Private, query).pipe(
      map((bookmarks) => {
        // restrict to bookmark's owned
        return bookmarks.filter((bookmark) => bookmark.is_owner);
      }),
    );
  }

  /* Gets a list of user custom conditions.
   */
  getPrivateConditions(query = ''): Observable<BookmarkModel[]> {
    return this.getUserConditions(BookmarkCategory.Private);
  }

  /**
   * Gets a list of bookmarks for the specified organziation
   * @param organizationId The organization id for the bookmarks
   * @param query: Optional search query
   */
  getOrganizationBookmarks(
    organizationId: number,
    query: string = '',
  ): Observable<BookmarkModel[]> {
    return this.getUserBookmarks(BookmarkCategory.Private, query).pipe(
      map((bookmarks) => {
        // filter enterprise bookmarks
        return (
          bookmarks
            .filter((bookmark) => bookmark.tags.includes('enterprise'))
            // restrict to group accessibility
            .filter((bookmark) =>
              bookmark.group_access.some((access) => access.group_id === organizationId),
            )
        );
      }),
    );
  }

  /**
   * Gets a list of group custom conditions.
   */
  getOrganizationConditions(organizationId: number, query = '') {
    return this.getUserConditions(BookmarkCategory.Group, query, organizationId);
  }

  /**
   * Gets all content (bookmarks, modules) for the specified organization
   */
  getOrganizationContent(organizationId: number, query: string = '') {
    return forkJoin([
      this.getOrganizationBookmarks(organizationId, query),
      this.getOrganizationConditions(organizationId, query),
    ]).pipe(map(([bookmarks, conditions]) => [...bookmarks, ...conditions]));
  }

  /**
   * Gets all private content (bookmarks, modules) for the current user
   * @param query
   * @returns
   */
  getPrivateContent(query: string = '') {
    return forkJoin([this.getPrivateBookmarks(query), this.getPrivateConditions(query)]).pipe(
      map(([bookmarks, conditions]) => [...bookmarks, ...conditions]),
    );
  }

  // create a new bookmark object
  createBookmarkStory(chapterIdMap): { story: StoryModel } {
    return this.serializeModuleService.serialize(
      {},
      new TrimConfiguration(),
      false,
      this.store.selectSnapshot(StoryState.getStory),
      this.store.selectSnapshot(StoryChaptersState.getStoryChapters),
      chapterIdMap,
    );
  }

  /**
   * Gets a list of public bookmarks
   * @param query: Optional search query
   */
  getPublicBookmarks(query: string = ''): Observable<BookmarkModel[]> {
    return this.getUserBookmarks(BookmarkCategory.Public, query);
  }

  saveBookmark(entry: BookmarkEntryModel): Observable<BookmarkModel> {
    // normalize to required service params
    const normalizedEntry = this.getNormalizedBookmarkEntry(entry);

    let response$: Observable<any>;
    if (entry.id) {
      // update bookmark
      response$ = this.http.post(`${bookmarkServiceURL}/update`, normalizedEntry, formHeaders);
    } else {
      // save new bookmark
      response$ = this.http.post(`${bookmarkServiceURL}/save`, normalizedEntry, formHeaders);
    }

    return response$.pipe(
      // normalize to bookmark model
      flatMap((response: any) => this.getBookmarkModel(response.id)),
    );
  }

  deleteBookmark(id: string): Observable<any> {
    return this.http.delete(`${bookmarkServiceURL}/delete?eid=${id}`).pipe(
      catchError((response) => {
        const { error } = response.error;
        const message = error ? error.message : '';
        return throwError(message);
      }),
    );
  }

  copyBookmark(id: string, type: ContentType, lang: string = 'en') {
    // Fix module id truncation
    if (type === ContentType.Module && !endsWith(id, '.json')) {
      id += '.json';
    }

    return this.http
      .post(`${bookmarkServiceURL}/copy`, { source_content_type: type, source_content_id: id })
      .pipe(
        map((response: any) => response.id),
        flatMap((bookmarkId) => {
          // translate bookmark
          return lang === 'en'
            ? of(bookmarkId) // English: copy as-is
            : this.translateBookmark(bookmarkId, id, type, lang); // translate into target lang
        }),
      );
  }

  atMaxFreeBookmarkCount(): Observable<BookmarkLimitModel> {
    return this.http.get<BookmarkLimitModel>(`/ws/user/content/limit/check?version=2`).pipe(
      catchError((err) => {
        return of({ limit_reached: false });
      }),
    );
  }

  /**
   * Filters the specified list by tags
   * @param bookmarks The list of bookmarks
   * @param tags The list of tags
   * @param inclusive True to match all tags, else single match
   */
  filterByTags(
    bookmarks: BookmarkModel[],
    tags: string[],
    inclusive: boolean = false,
  ): BookmarkModel[] {
    return bookmarks.filter((bookmark) => {
      if (inclusive) {
        return tags.every((tag) => bookmark.tags.includes(tag));
      } else {
        return tags.some((tag) => bookmark.tags.includes(tag));
      }
    });
  }

  /**
   * For the specified bookmark id, translate using the source bookmark | module
   */
  private translateBookmark(
    bookmarkId: string,
    sourceBookmarkId: string,
    sourceBookmarkType: ContentType,
    sourceLang: string,
  ): Observable<string> {
    // normalize ID
    const normalizedSourceBookmarkId =
      sourceBookmarkType === ContentType.Module || sourceBookmarkType === ContentType.Internal
        ? sourceBookmarkId.replace('.json', '')
        : sourceBookmarkId;

    return forkJoin([
      // get story
      this.storyService.getStory(bookmarkId, ContentType.Bookmark),
      // get source translation
      this.storyService.getStoryTranslation(
        normalizedSourceBookmarkId,
        sourceBookmarkType,
        sourceLang,
      ),
    ]).pipe(
      // translate story
      map(([bookmarkStory, sourceTranslation]) =>
        this.storyService.translateStory(bookmarkStory, sourceTranslation),
      ),
      // update bookmark w/ translated story
      flatMap((translatedBookmarkStory) => {
        return this.saveBookmark({
          isOwner: true,
          // NOTE: Bookmark service restriction: title (maxlength = 60)
          title: translatedBookmarkStory.displayName.substr(0, 60),
          id: bookmarkId,
          index: translatedBookmarkStory,
        });
      }),
      map((bookmark) => bookmark.id),
      // capture 404 request
      catchError((err: HttpErrorResponse) =>
        err.status === 404 ? of(bookmarkId) : throwError(err),
      ),
    );
  }

  private getNormalizedBookmarkEntry(entry: BookmarkEntryModel): any {
    // optional user (owner) fields
    let group_access: any;
    let publicAccess: string;
    let flags: { [key: string]: string };
    let is_content_copy: string;

    if (entry.isOwner) {
      // set additional group access
      group_access = entry.add_group_access ? this.getGroupAccess(entry.add_group_access) : [];

      // revoke group access
      if (entry.remove_group_access) {
        entry.remove_group_access.forEach((access) => {
          const removedAccess = this.getNormalizeGroupAccess({
            group_id: access.group_id,
            remove: true,
          });
          group_access.push(removedAccess);
        });
      }

      // set public access
      publicAccess = this.getNormalizedBool(entry.public_access);

      // custom flags
      if (entry.index) {
        // init flags
        flags = {};

        // flag: user generated tours
        if (entry.index.chapters) {
          const isTour = entry.index.chapters.length > 1;
          flags[BookmarkFlagsEnum.UserGeneratedTour] = this.getNormalizedBool(isTour);
        }
        // flag: animated content
        const isAnimated = (entry.index.tags || []).includes('animated');
        flags[BookmarkFlagsEnum.Animated] = this.getNormalizedBool(isAnimated);

        // flags: quiz, content only mode
        flags[BookmarkFlagsEnum.ModeQuiz] = this.getNormalizedBool(
          entry.viewMode === BookmarkViewModeTag.Quiz,
        );
        flags[BookmarkFlagsEnum.ModeContent] = this.getNormalizedBool(
          entry.viewMode === BookmarkViewModeTag.Content,
        );

        // flag: quiz
        const isQuiz = get(entry.index, 'quizSettings.enabled', false);
        flags[BookmarkFlagsEnum.Quiz] = this.getNormalizedBool(isQuiz);
      }

      // set content copy flag
      is_content_copy = this.getNormalizedBool(entry.is_content_copy);
    }

    // normalized service entry
    return {
      eid: entry.id,
      name: entry.title,
      description: entry.description,
      // serialized story
      index: entry.index,
      // permissions: set null for no updates
      group_access: group_access,
      public_access: publicAccess,
      // custom bookmark flags
      flags: flags,
      // content copy flag
      is_content_copy: is_content_copy,
    };
  }

  private getGroupAccess(groupAccess: BookmarkGroupModel[]): any {
    return groupAccess.map((access) => this.getNormalizeGroupAccess(access));
  }

  private getNormalizeGroupAccess(access: BookmarkGroupModel): any {
    const { group_id, group_name, member_edit, visible, remove } = access;

    const normalized = {
      group_id: group_id,
      group_name: group_name,
      member_edit: this.getNormalizedBool(member_edit),
      visibility: this.getNormalizedBool(visible),
      remove: this.getNormalizedBool(remove),
    };
    return normalized;
  }

  private getNormalizedBool(value: boolean): string {
    return value === true ? '1' : value === false ? '0' : '';
  }
}
